'use client';

import { useEffect } from 'react';
import AppImage from '@/components/ui/AppImage';
import Icon from '@/components/ui/AppIcon';

interface QuickViewProduct {
  id: string;
  name: string;
  category: string;
  manufacturer: string;
  image: string;
  alt: string;
  price: string;
  compliance: string[];
  rating: number;
  description: string;
  specifications: {
    label: string;
    value: string;
  }[];
  features: string[];
  inStock: boolean;
}

interface QuickViewModalProps {
  product: QuickViewProduct | null;
  isOpen: boolean;
  onClose: () => void;
  onRequestQuote: (id: string) => void;
}

export default function QuickViewModal({
  product,
  isOpen,
  onClose,
  onRequestQuote,
}: QuickViewModalProps) {
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }

    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
    }

    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, onClose]);

  if (!isOpen || !product) return null;

  return (
    <>
      <div
        className="fixed inset-0 bg-black bg-opacity-50 z-50 transition-opacity duration-300"
        onClick={onClose}
      />
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
        <div
          className="bg-card rounded-lg shadow-lg max-w-5xl w-full max-h-[90vh] overflow-y-auto"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="sticky top-0 bg-card border-b border-border px-6 py-4 flex items-center justify-between z-10">
            <h2 className="text-2xl font-bold text-text-primary">Quick View</h2>
            <button
              onClick={onClose}
              className="p-2 hover:bg-muted rounded-full transition-colors duration-250"
              aria-label="Close modal"
            >
              <Icon name="XMarkIcon" size={24} className="text-text-secondary" />
            </button>
          </div>

          <div className="p-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              <div>
                <div className="relative h-96 bg-muted rounded-lg overflow-hidden mb-4">
                  <AppImage
                    src={product.image}
                    alt={product.alt}
                    className="w-full h-full object-cover"
                  />
                  {!product.inStock && (
                    <div className="absolute inset-0 bg-black bg-opacity-50 flex items-center justify-center">
                      <span className="bg-error text-error-foreground px-6 py-3 rounded-md font-semibold text-lg">
                        Out of Stock
                      </span>
                    </div>
                  )}
                </div>

                <div className="flex flex-wrap gap-2">
                  {product.compliance.map((cert) => (
                    <span
                      key={cert}
                      className="inline-flex items-center gap-1 px-3 py-1.5 bg-success bg-opacity-10 text-success text-sm font-medium rounded"
                    >
                      <Icon name="CheckBadgeIcon" size={16} variant="solid" />
                      {cert}
                    </span>
                  ))}
                </div>
              </div>

              <div>
                <p className="text-sm text-text-secondary uppercase tracking-wide mb-2">
                  {product.category}
                </p>
                <h3 className="text-3xl font-bold text-text-primary mb-2">{product.name}</h3>
                <p className="text-lg text-text-secondary mb-4">{product.manufacturer}</p>

                <div className="flex items-center gap-2 mb-6">
                  <div className="flex items-center gap-1">
                    {[...Array(5)].map((_, index) => (
                      <Icon
                        key={index}
                        name="StarIcon"
                        size={20}
                        variant={index < Math.floor(product.rating) ? 'solid' : 'outline'}
                        className={
                          index < Math.floor(product.rating) ? 'text-warning' : 'text-gray-300'
                        }
                      />
                    ))}
                  </div>
                  <span className="text-sm text-text-secondary">({product.rating} rating)</span>
                </div>

                <div className="bg-muted p-4 rounded-lg mb-6">
                  <p className="text-sm text-text-secondary mb-1">Starting at</p>
                  <p className="text-4xl font-bold text-primary">{product.price}</p>
                </div>

                <p className="text-text-primary mb-6 leading-relaxed">{product.description}</p>

                <div className="mb-6">
                  <h4 className="font-semibold text-text-primary mb-3">Key Features</h4>
                  <ul className="space-y-2">
                    {product.features.map((feature, index) => (
                      <li key={index} className="flex items-start gap-2">
                        <Icon
                          name="CheckCircleIcon"
                          size={20}
                          className="text-success flex-shrink-0 mt-0.5"
                          variant="solid"
                        />
                        <span className="text-sm text-text-primary">{feature}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                <div className="mb-6">
                  <h4 className="font-semibold text-text-primary mb-3">Specifications</h4>
                  <div className="space-y-2">
                    {product.specifications.map((spec, index) => (
                      <div
                        key={index}
                        className="flex justify-between py-2 border-b border-border last:border-0"
                      >
                        <span className="text-sm text-text-secondary">{spec.label}</span>
                        <span className="text-sm font-medium text-text-primary">{spec.value}</span>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="flex gap-3">
                  <button
                    onClick={() => onRequestQuote(product.id)}
                    disabled={!product.inStock}
                    className="flex-1 px-6 py-3 bg-brand-cta text-white font-semibold rounded-lg hover:bg-red-700 transition-all duration-250 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Request Quote
                  </button>
                  <button className="px-6 py-3 border border-border text-text-primary font-semibold rounded-lg hover:bg-muted transition-all duration-250">
                    Download Specs
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}